/**
 * AppGate — affiche le contenu uniquement si l'app est installée ET active.
 * Sinon, affiche un CTA vers l'AppStore avec des infos sur l'app.
 */
import { Link } from 'react-router-dom';
import { useAppInstalled } from '@/hooks/useAppInstalled';

interface AppGateProps {
  nompage: string;
  appName: string;
  appDescription?: string;
  icon?: string;
  children: React.ReactNode;
}

export default function AppGate({ nompage, appName, appDescription, icon = 'ri-apps-2-line', children }: AppGateProps) {
  const { installed, active, loading } = useAppInstalled(nompage);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (!installed || !active) {
    return (
      <div className="p-4 md:p-8 flex flex-col items-center justify-center min-h-[70vh] text-center">
        <div className="w-20 h-20 rounded-2xl bg-primary-100 flex items-center justify-center mb-5">
          <i className={`${icon} text-4xl text-primary-600`}></i>
        </div>
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">
          {installed ? `${appName} est désactivé` : `${appName} n'est pas installé`}
        </h2>
        {appDescription && (
          <p className="text-sm text-foreground-500 max-w-md mb-6 leading-relaxed">{appDescription}</p>
        )}
        {!installed ? (
          <div className="space-y-3">
            <p className="text-sm text-foreground-600">
              Installez <strong>{appName}</strong> depuis l'AppStore pour activer ce module.
            </p>
            <Link
              to="/dashboard/appstore"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
            >
              <i className="ri-store-2-line"></i>
              Accéder à l'AppStore
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-foreground-600">
              Activez <strong>{appName}</strong> dans vos applications pour utiliser ce module.
            </p>
            <Link
              to="/dashboard/apps"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
            >
              <i className="ri-toggle-fill"></i>
              Mes applications
            </Link>
          </div>
        )}
      </div>
    );
  }

  return <>{children}</>;
}